import React, { useState, useEffect } from 'react'
import { backendService, DoctorSummary, TokenBalance, TokenTransaction } from '../../services/backendService'
import { logInfo, logError } from '../../utils/errorLogger'
import './BalanceManagement.css'

export const BalanceManagement: React.FC = () => {
  const [doctors, setDoctors] = useState<DoctorSummary[]>([])
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorSummary | null>(null)
  const [balance, setBalance] = useState<TokenBalance | null>(null)
  const [transactions, setTransactions] = useState<TokenTransaction[]>([])
  const [transactionPage, setTransactionPage] = useState(1)
  const [totalTransactionPages, setTotalTransactionPages] = useState(1)
  
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false)
  const [isLoadingBalance, setIsLoadingBalance] = useState(false)
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const [searchTerm, setSearchTerm] = useState('')
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [transactionType, setTransactionType] = useState<'credit' | 'debit'>('credit')
  
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Load doctors on mount
  useEffect(() => {
    loadDoctors()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load balance and transactions when doctor is selected
  useEffect(() => {
    if (selectedDoctor) {
      loadBalance()
      loadTransactions(1)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctor])

  const loadDoctors = async () => {
    setIsLoadingDoctors(true)
    try {
      const response = await backendService.listAllDoctors(1, 100, searchTerm, 'name', 'asc', true)
      setDoctors(response.doctors)
      logInfo(`Loaded ${response.doctors.length} doctors`, 'BalanceManagement')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load doctors'
      logError(`Failed to load doctors: ${errorMessage}`, 'BalanceManagement')
    } finally {
      setIsLoadingDoctors(false)
    }
  }

  const loadBalance = async () => {
    if (!selectedDoctor) return
    
    setIsLoadingBalance(true)
    try {
      const balanceData = await backendService.getDoctorTokenBalance(selectedDoctor.id)
      setBalance(balanceData)
      logInfo(`Loaded balance for ${selectedDoctor.name}: ${balanceData.balance} tokens`, 'BalanceManagement')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load balance'
      logError(`Failed to load balance: ${errorMessage}`, 'BalanceManagement')
    } finally {
      setIsLoadingBalance(false)
    }
  }

  const loadTransactions = async (page: number) => {
    if (!selectedDoctor) return
    
    setIsLoadingTransactions(true)
    try {
      const response = await backendService.getDoctorTokenHistory(selectedDoctor.id, page, 10)
      setTransactions(response.transactions)
      setTransactionPage(page)
      setTotalTransactionPages(response.totalPages)
      logInfo(`Loaded ${response.transactions.length} transactions for ${selectedDoctor.name}`, 'BalanceManagement')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load transactions'
      logError(`Failed to load transactions: ${errorMessage}`, 'BalanceManagement')
    } finally {
      setIsLoadingTransactions(false)
    }
  }

  const handleDoctorSelect = (doctor: DoctorSummary) => {
    setSelectedDoctor(doctor)
    setBalance(null)
    setTransactions([])
    setAmount('')
    setDescription('')
    setSubmitSuccess(false)
    setSubmitError(null)
    logInfo(`Selected doctor: ${doctor.name} (${doctor.username})`, 'BalanceManagement')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedDoctor) {
      setSubmitError('Please select a doctor first')
      return
    }

    const amountNum = parseInt(amount)
    if (isNaN(amountNum) || amountNum <= 0) {
      setSubmitError('Please enter a valid positive amount')
      return
    }

    if (!description.trim()) {
      setSubmitError('Please enter a description')
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      const response = await backendService.addDoctorTokens(selectedDoctor.id, {
        amount: amountNum,
        description: description.trim(),
        transactionType
      })

      logInfo(`Tokens ${transactionType === 'credit' ? 'added' : 'deducted'} successfully. New balance: ${response.newBalance}`, 'BalanceManagement')

      setSubmitSuccess(true)
      setAmount('')
      setDescription('')

      // Reload balance and transactions
      await loadBalance()
      await loadTransactions(1)

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false)
      }, 3000)

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update tokens'
      logError(`Failed to update tokens: ${errorMessage}`, 'BalanceManagement')
      setSubmitError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const filteredDoctors = doctors.filter(doctor =>
    doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doctor.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doctor.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="balance-management">
      <div className="balance-layout">
        {/* Left Panel: Doctor Selection */}
        <div className="doctor-selector-panel">
          <h3 className="panel-title">Select Doctor</h3>
          
          <div className="search-box">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, username, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isLoadingDoctors ? (
            <div className="loading-state">
              <span className="spinner"></span>
              <span>Loading doctors...</span>
            </div>
          ) : (
            <div className="doctor-list">
              {filteredDoctors.length === 0 ? (
                <div className="empty-state">
                  <p>No doctors found</p>
                </div>
              ) : (
                filteredDoctors.map(doctor => (
                  <div
                    key={doctor.id}
                    className={`doctor-item ${selectedDoctor?.id === doctor.id ? 'selected' : ''}`}
                    onClick={() => handleDoctorSelect(doctor)}
                  >
                    <div className="doctor-info">
                      <div className="doctor-name">{doctor.name}</div>
                      <div className="doctor-username">@{doctor.username}</div>
                    </div>
                    <div className="doctor-balance">
                      <span className="balance-amount">{doctor.balance}</span>
                      <span className="balance-label">tokens</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Right Panel: Balance Management */}
        <div className="balance-details-panel">
          {!selectedDoctor ? (
            <div className="empty-state-large">
              <div className="empty-icon">👈</div>
              <h3>Select a Doctor</h3>
              <p>Choose a doctor from the list to manage their token balance</p>
            </div>
          ) : (
            <>
              {/* Success/Error Messages */}
              {submitSuccess && (
                <div className="alert alert-success">
                  <span className="alert-icon">✅</span>
                  <div className="alert-content">
                    <strong>Success!</strong> Tokens updated successfully.
                  </div>
                </div>
              )}

              {submitError && (
                <div className="alert alert-error">
                  <span className="alert-icon">❌</span>
                  <div className="alert-content">
                    <strong>Error:</strong> {submitError}
                  </div>
                </div>
              )}

              {/* Current Balance Card */}
              <div className="balance-card">
                <h3 className="card-title">Current Balance</h3>
                <div className="doctor-header">
                  <div className="doctor-name-large">{selectedDoctor.name}</div>
                  <div className="doctor-meta">
                    @{selectedDoctor.username} • {selectedDoctor.email}
                  </div>
                </div>

                {isLoadingBalance ? (
                  <div className="loading-state">
                    <span className="spinner"></span>
                    <span>Loading balance...</span>
                  </div>
                ) : balance ? (
                  <div className="balance-stats">
                    <div className="stat-item stat-primary">
                      <div className="stat-label">Current Balance</div>
                      <div className="stat-value">{balance.balance}</div>
                      <div className="stat-unit">tokens</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Total Earned</div>
                      <div className="stat-value">{balance.totalEarned}</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-label">Total Spent</div>
                      <div className="stat-value">{balance.totalSpent}</div>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Add/Deduct Tokens Form */}
              <div className="balance-form-card">
                <h3 className="card-title">Manage Tokens</h3>
                <form onSubmit={handleSubmit} className="balance-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="transactionType" className="form-label">
                        Transaction Type
                      </label>
                      <select
                        id="transactionType"
                        className="form-input"
                        value={transactionType}
                        onChange={(e) => setTransactionType(e.target.value as 'credit' | 'debit')}
                        disabled={isSubmitting}
                      >
                        <option value="credit">Add Tokens (Credit)</option>
                        <option value="debit">Deduct Tokens (Debit)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="amount" className="form-label">
                        Amount <span className="required">*</span>
                      </label>
                      <input
                        id="amount"
                        type="number"
                        className="form-input"
                        placeholder="100"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        disabled={isSubmitting}
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="description" className="form-label">
                      Description <span className="required">*</span>
                    </label>
                    <input
                      id="description"
                      type="text"
                      className="form-input"
                      placeholder="Monthly token allocation"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Processing...
                      </>
                    ) : (
                      transactionType === 'credit' ? 'Add Tokens' : 'Deduct Tokens'
                    )}
                  </button>
                </form>
              </div>

              {/* Transaction History */}
              <div className="transaction-history-card">
                <h3 className="card-title">Transaction History</h3>

                {isLoadingTransactions ? (
                  <div className="loading-state">
                    <span className="spinner"></span>
                    <span>Loading transactions...</span>
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="empty-state">
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <>
                    <div className="transaction-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Amount</th>
                            <th>Description</th>
                            <th>Balance After</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map(transaction => (
                            <tr key={transaction.id}>
                              <td className="date-cell">
                                {new Date(transaction.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td>
                                <span className={`transaction-type ${transaction.transactionType}`}>
                                  {transaction.transactionType === 'credit' ? '+ Credit' : '- Debit'}
                                </span>
                              </td>
                              <td className={`amount-cell ${transaction.transactionType}`}>
                                {transaction.transactionType === 'credit' ? '+' : '-'}{transaction.amount}
                              </td>
                              <td className="description-cell">{transaction.description}</td>
                              <td className="balance-cell">{transaction.balanceAfter}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalTransactionPages > 1 && (
                      <div className="pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => loadTransactions(transactionPage - 1)}
                          disabled={transactionPage === 1 || isLoadingTransactions}
                        >
                          ← Previous
                        </button>
                        <span className="pagination-info">
                          Page {transactionPage} of {totalTransactionPages}
                        </span>
                        <button
                          className="pagination-btn"
                          onClick={() => loadTransactions(transactionPage + 1)}
                          disabled={transactionPage === totalTransactionPages || isLoadingTransactions}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

